import { useToast } from 'vue-toastification';
import store from '@/store';
import { demoFilterGridOptions } from '@/utils/demoGridFilters';

const BLOCKED_MESSAGES = {
  export: 'Exports are disabled while Demo Mode is on.',
  download: 'Downloads are disabled while Demo Mode is on.',
  copy: 'Copying is disabled while Demo Mode is on.',
  open: 'Opening files is disabled while Demo Mode is on.',
  send: 'Sending to providers is disabled while Demo Mode is on.',
};

const CANCELLED_MESSAGE =
  'Demo Mode turned on before this finished, so it was cancelled.';

export function sensitiveEgressBlocked() {
  return store.getters['demoMode/sensitiveEgressBlocked'] !== false;
}

function currentGeneration() {
  return store.getters['demoMode/generation'];
}

function notify(message) {
  try {
    useToast().warning(message);
  } catch {
    if (typeof window !== 'undefined' && typeof window.alert === 'function') {
      window.alert(message);
    }
  }
}

export function blockSensitiveEgress(kind = 'export') {
  if (!sensitiveEgressBlocked()) return false;
  notify(BLOCKED_MESSAGES[kind] || BLOCKED_MESSAGES.export);
  return true;
}

export function exportBlockedInDemoMode() {
  return blockSensitiveEgress('export');
}

export function beginSensitiveEgress(kind = 'export') {
  if (blockSensitiveEgress(kind)) return null;
  const generation = currentGeneration();
  let cancelledNotified = false;
  return {
    kind,
    get active() {
      return !sensitiveEgressBlocked() && currentGeneration() === generation;
    },
    confirm() {
      if (this.active) return true;
      if (!cancelledNotified) {
        cancelledNotified = true;
        notify(CANCELLED_MESSAGE);
      }
      return false;
    },
  };
}

function ticketAllows(ticket, kind) {
  if (ticket) return ticket.confirm();
  return !blockSensitiveEgress(kind);
}

export function downloadSensitiveBlob(blob, filename, ticket = null) {
  if (!ticketAllows(ticket, 'download')) return false;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 0);
  return true;
}

// Like ag-grid's own fallback, hand focus back afterwards so keyboard
// navigation in a grid keeps working after Ctrl/Cmd+C.
function legacyCopy(text) {
  const focusedBefore = document.activeElement;
  const el = document.createElement('textarea');
  el.value = text;
  el.setAttribute('readonly', '');
  el.style.position = 'fixed';
  el.style.opacity = '0';
  document.body.appendChild(el);
  el.select();
  try {
    return document.execCommand('copy');
  } finally {
    document.body.removeChild(el);
    if (focusedBefore && focusedBefore !== document.body) {
      focusedBefore.focus?.({ preventScroll: true });
    }
  }
}

export async function copySensitiveText(text, ticket = null) {
  if (!ticketAllows(ticket, 'copy')) return false;
  const value = text == null ? '' : String(text);
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return true;
  }
  return legacyCopy(value);
}

export function openSensitiveUrl(url, target = '_blank', ticket = null) {
  if (!ticketAllows(ticket, 'open')) return false;
  window.open(url, target, 'noopener');
  return true;
}

export const demoSafeGridOptions = {
  // Registers every grid so filters on identity columns are cleared and
  // masked while Demo Mode is on (see demoGridFilters.js).
  ...demoFilterGridOptions,
  // Replacing ag-grid's own clipboard write also replaces its execCommand
  // fallback (unfocused window, restrictive permission policy), so keep one.
  sendToClipboard({ data }) {
    copySensitiveText(data).catch(() => {
      if (!sensitiveEgressBlocked()) legacyCopy(data);
    });
  },
  getContextMenuItems(params) {
    const items = params.defaultItems || [];
    if (!sensitiveEgressBlocked()) return items;
    return items.filter(
      (item) =>
        typeof item !== 'string' ||
        !/^(copy|cut|paste|export|csvExport|excelExport)/.test(item)
    );
  },
  defaultCsvExportParams: {
    shouldRowBeSkipped: () => sensitiveEgressBlocked(),
  },
  defaultExcelExportParams: {
    shouldRowBeSkipped: () => sensitiveEgressBlocked(),
  },
};
